import React, { useCallback } from "react";

import Button from "components/ui/button/Button";
import toast from "components/ui/toast/Toast";
import { useCreateBid } from "lib/hooks/api/bid/bids/useCreateBid";
import { useCurrentUser } from "lib/hooks/api/users/useCurrentUser";
import { CreateBidFormOutput } from "../../../../../components/bids/create_bid/CreateBidForm";
import { createBidSchema } from "../../../../../components/bids/utils";
import { routePaths } from "../../../../routes";
import { EventData, EventNames, useTracking } from "../../../../tracking";
import { useDefaultBidWorkflowStage } from "./useDefaultBidWorkflowStage";

type CreateBidProps = {
  defaultValues: Partial<CreateBidFormOutput>;
  createdFrom: "notice" | "lead";
  trackingData: EventData;
  onCreateBid?: (bidId: string) => void;
};

type CreateBidOneClick = {
  isLoading: boolean;
  isDisabled: boolean;
  createBidFromDefaultValues: (e: React.MouseEvent<HTMLElement>) => void;
};

export function useCreateBidOneClick({
  createdFrom,
  defaultValues,
  trackingData,
  onCreateBid,
}: CreateBidProps): CreateBidOneClick {
  const { logEvent } = useTracking();
  const { data: currentUser } = useCurrentUser();
  const { defaultStageId, isLoading: isLoadingDefaultStage } =
    useDefaultBidWorkflowStage(defaultValues);

  const { mutate: createBid, isLoading: isCreatingBid } = useCreateBid(trackingData, {
    onSuccess: (data) => {
      toast.message(`New Bid created from ${createdFrom}`, {
        action: (
          <Button as="a" to={routePaths.bidDetail} params={{ id: data.createBid.id }}>
            View
          </Button>
        ),
      });
      logEvent(EventNames.saveDataClicked, { "Data type": createdFrom, ...trackingData });
      onCreateBid?.(data.createBid.id);
    },
    onError: () => {
      toast.error("Failed to create bid");
    },
  });

  const createBidFromDefaultValues = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      e.stopPropagation();

      if (!defaultStageId) {
        toast.error("Could not determine default stage for bid");
        return;
      }

      const { entities, ...bid } = createBidSchema.parse({
        ...defaultValues,
        stageId: defaultValues.stageId ?? defaultStageId,
        assignedToId: defaultValues.assignedToId ?? currentUser?.guid,
      });

      createBid({
        bid: {
          ...bid,
        },
        entities: { entities: entities ?? [] },
      });
    },
    [createBid, currentUser, defaultStageId, defaultValues],
  );

  return {
    isLoading: isCreatingBid || isLoadingDefaultStage,
    isDisabled: isCreatingBid || isLoadingDefaultStage || !defaultStageId,
    createBidFromDefaultValues,
  };
}
